// Shapes mirroring both the Postgres schema and Swift's SPDomain.
//
// Where a value ends up inside a published snapshot (blocks, criterion, steps) the
// encoding must match Swift's synthesised Codable exactly — enum payloads live under
// `_0`. `SPDomainTests/ServerContractTests` is what proves that agreement; these types
// exist so TypeScript catches drift at author time too.

export type Uuid = string;

export type AppRole = 'super_admin' | 'module_owner';

export type ModuleCategory = 'Strength' | 'Cardio' | 'Mobility' | 'Recovery' | 'Flow';
export type Difficulty = 'Beginner' | 'Intermediate' | 'Advanced';

export const MEASUREMENT_DIMENSIONS = [
    'repetitions', 'duration', 'externalLoad', 'distance', 'rounds',
    'jointAngle', 'bodyweightRatio', 'gripperClass', 'perceivedExertion',
] as const;
export type MeasurementDimension = (typeof MEASUREMENT_DIMENSIONS)[number];

/** Display names — never derive these from the raw case (`externalLoad` → "Externalload"). */
export const DIMENSION_LABELS: Record<MeasurementDimension, string> = {
    repetitions: 'Reps',
    duration: 'Time',
    externalLoad: 'Load',
    distance: 'Distance',
    rounds: 'Rounds',
    jointAngle: 'Angle',
    bodyweightRatio: 'Bodyweight',
    gripperClass: 'Gripper',
    perceivedExertion: 'RPE',
};

// --- Swift-encoded unions --------------------------------------------------------

/** SetGrouping. Cases without payloads encode as `{ case: {} }`. */
export type SetGrouping =
    | { straight: Record<string, never> }
    | { superset: Record<string, never> }
    | { circuit: { rounds: number } }
    | { dropSet: Record<string, never> }
    | { pyramid: { ascending: boolean } }
    | { flow: Record<string, never> };

/** Target.Bound. */
export type Bound =
    | { exactly: { _0: number } }
    | { between: { _0: number; _1: number } }
    | { atLeast: { _0: number } }
    | { toFailure: Record<string, never> };

export interface Target {
    dimension: MeasurementDimension;
    bound: Bound;
}

export interface Quantity {
    dimension: MeasurementDimension;
    value: number;
}

export interface SetPrescription {
    targets: Target[];
    restAfter?: Quantity | null;
    tempo?: string | null;
}

/** CompletionMode — the per-item recording decision (spec §8.4). */
export type CompletionMode =
    | { automatic: Record<string, never> }
    | { recorded: { _0: { dimensions: MeasurementDimension[]; isRequired: boolean } } };

export interface ExercisePrescription {
    id: string;
    exerciseID: Uuid;
    sets: SetPrescription[];
    completion: CompletionMode;
    /** Non-optional in Swift, so it must be present even when empty. */
    notes: string;
}

export interface ExerciseBlock {
    id: string;
    title?: string | null;
    grouping: SetGrouping;
    exercises: ExercisePrescription[];
    restAfter?: Quantity | null;
}

/** Criterion — the recursive rule tree. */
export type Criterion =
    | { always: Record<string, never> }
    | { personalBest: { _0: Uuid; _1: MeasurementDimension; atLeast: number } }
    | { totalVolume: { _0: Uuid; _1: MeasurementDimension; atLeast: number; within: DateWindow } }
    | { sessionsCompleted: { atLeast: number; program: string | null } }
    | { completedSession: { _0: Uuid } }
    | { allOf: { _0: Criterion[] } }
    | { anyOf: { _0: Criterion[] } }
    | { not: { _0: Criterion } };

export interface DateWindow {
    start?: string | null;
    end?: string | null;
}

// --- Rows ------------------------------------------------------------------------

export interface Module {
    id: Uuid;
    slug: string;
    name: string;
    studio_name: string;
    tagline: string;
    parameters: string;
    category: ModuleCategory;
    difficulty: Difficulty;
    icon: string;
    live_version_id: Uuid | null;
    updated_at: string;
}

export interface Exercise {
    id: Uuid;
    module_id: Uuid;
    slug: string;
    name: string;
    instructions: string;
    tags: string[];
    measured_in: MeasurementDimension[];
    image_media_id?: Uuid | null;
    demo_media_id?: Uuid | null;
}

export interface Session {
    id: Uuid;
    module_id: Uuid;
    slug: string;
    title: string;
    summary: string;
    tags: string[];
    blocks: ExerciseBlock[];
    estimated_seconds: number | null;
    is_free_sample: boolean;
    attribute_weights: Record<string, number>;
    cover_media_id?: Uuid | null;
    video_media_id?: Uuid | null;
}

export interface ProgramNode {
    id: Uuid;
    module_id: Uuid;
    parent_id: Uuid | null;
    title: string;
    sort_order: number;
    session_id: Uuid | null;
}

export interface Program {
    module_id: Uuid;
    title: string;
    summary: string;
    pacing: 'scheduled' | 'selfPaced' | 'skillGated' | 'library';
    root_id: Uuid | null;
}

export interface Skill {
    id: Uuid;
    module_id: Uuid;
    slug: string;
    name: string;
    summary: string;
    tags: string[];
    tier: number;
    criterion: Criterion;
    exercise_id: Uuid | null;
    prerequisites: Uuid[];
    image_media_id?: Uuid | null;
    demo_media_id?: Uuid | null;
}

export interface SkillStudyStep {
    caption: string;
    mediaSeed: string;
}

export interface SkillStudy {
    id: Uuid;
    module_id: Uuid;
    slug: string;
    title: string;
    difficulty: Difficulty;
    coaching_notes: string;
    duration_seconds: number;
    steps: SkillStudyStep[];
    exercise_id: Uuid | null;
    is_free_sample: boolean;
    sort_order: number;
}

/** One row of media_assets: a Storage-backed image or a Mux-backed video. */
export interface MediaAsset {
    id: Uuid;
    module_id: Uuid;
    kind: 'video' | 'image' | 'audio';
    status: 'uploading' | 'processing' | 'ready' | 'errored';
    mux_playback_id: string | null;
    storage_path: string | null;
    duration_seconds: number | null;
    title: string;
}

export interface ModuleVersion {
    id: Uuid;
    module_id: Uuid;
    version: number;
    published_at: string;
    notes: string;
}

export interface CurrentUser {
    id: Uuid;
    email: string;
    roles: AppRole[];
    ownedModuleIds: Uuid[];
}

export function isSuperAdmin(user: CurrentUser | null): boolean {
    return user?.roles.includes('super_admin') ?? false;
}

export function canEditModule(user: CurrentUser | null, moduleId: Uuid): boolean {
    if (!user) return false;
    return isSuperAdmin(user) || user.ownedModuleIds.includes(moduleId);
}
